import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CheckCircle2, Lightbulb, MessageSquareHeart, Sparkles, Star } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { supabase } from "@/integrations/supabase/client";
import {
  buildReferenceCode,
  CONTENT_SUGGESTION_TYPES,
  deriveAccountRoleContext,
  feedbackSchema,
  feedbackTypeOptions,
  FEEDBACK_STATUS_LABELS,
  importanceOptions,
  ratingOptions,
  sentimentOptions,
  type FeedbackStatus,
  type FeedbackType,
} from "@/lib/support-center";
import { cn } from "@/lib/utils";

type FeedbackRow = {
  id: string;
  subject: string;
  feedback_type: FeedbackType;
  status: FeedbackStatus;
  created_at: string;
  updated_at: string;
  message: string;
  rating: number | null;
  sentiment: number | null;
  importance_level: number | null;
  course_id: string | null;
  lesson_id: string | null;
  module_key: string | null;
};

type ContentSuggestionRow = {
  id: string;
  feedback_id: string;
  requested_topic: string;
  status: FeedbackStatus;
};

const statusStyles: Record<FeedbackStatus, string> = {
  new: "border-primary/30 bg-primary/10 text-primary",
  reviewed: "border-accent/30 bg-accent/10 text-accent",
  planned: "border-secondary/30 bg-secondary/10 text-secondary",
  completed: "border-border/80 bg-card text-foreground",
  dismissed: "border-border/70 bg-background/70 text-muted-foreground",
};

const FeedbackCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { household, isParent } = useHousehold();

  const [submitting, setSubmitting] = useState(false);
  const [successReference, setSuccessReference] = useState<string | null>(null);
  const [feedbackRows, setFeedbackRows] = useState<FeedbackRow[]>([]);
  const [contentSuggestions, setContentSuggestions] = useState<Record<string, ContentSuggestionRow>>({});
  const [loadingFeedback, setLoadingFeedback] = useState(true);
  const [formState, setFormState] = useState({
    feedbackType: (searchParams.get("type") as FeedbackType | null) || "general_feedback",
    subject: "",
    message: "",
    courseId: "",
    lessonId: "",
    moduleKey: "",
    rating: null as number | null,
    sentiment: null as number | null,
    importanceLevel: null as number | null,
  });

  const featuredFeedbackTypes = useMemo(() => feedbackTypeOptions.slice(0, 8), []);

  const loadFeedback = async () => {
    if (!user) return;
    setLoadingFeedback(true);

    const { data, error } = await supabase
      .from("user_feedback")
      .select("id, subject, feedback_type, status, created_at, updated_at, message, rating, sentiment, importance_level, course_id, lesson_id, module_key")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(12);

    if (error) {
      setLoadingFeedback(false);
      toast.error("Unable to load your feedback history.");
      return;
    }

    const items = (data || []) as FeedbackRow[];
    setFeedbackRows(items);

    if (items.length === 0) {
      setContentSuggestions({});
      setLoadingFeedback(false);
      return;
    }

    const { data: suggestionRows } = await supabase
      .from("content_suggestions")
      .select("id, feedback_id, requested_topic, status")
      .in("feedback_id", items.map((item) => item.id));

    const suggestionMap: Record<string, ContentSuggestionRow> = {};
    ((suggestionRows || []) as ContentSuggestionRow[]).forEach((suggestion) => {
      suggestionMap[suggestion.feedback_id] = suggestion;
    });

    setContentSuggestions(suggestionMap);
    setLoadingFeedback(false);
  };

  useEffect(() => {
    void loadFeedback();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [user]);

  useEffect(() => {
    const type = searchParams.get("type") as FeedbackType | null;
    if (type) {
      setFormState((current) => ({ ...current, feedbackType: type }));
    }
  }, [searchParams]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) {
      toast.error("You need to be signed in to send feedback.");
      return;
    }

    const parsed = feedbackSchema.safeParse(formState);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Please review your feedback and try again.");
      return;
    }

    setSubmitting(true);

    const { data, error } = await supabase
      .from("user_feedback")
      .insert({
        user_id: user.id,
        account_role: deriveAccountRoleContext({
          isParent,
          householdOwnerId: household?.owner_id,
          userId: user.id,
        }),
        feedback_type: parsed.data.feedbackType,
        subject: parsed.data.subject,
        message: parsed.data.message,
        course_id: parsed.data.courseId || null,
        lesson_id: parsed.data.lessonId || null,
        module_key: parsed.data.moduleKey || null,
        rating: parsed.data.rating ?? null,
        sentiment: parsed.data.sentiment ?? null,
        importance_level: parsed.data.importanceLevel ?? null,
        page_path: location.pathname,
        metadata: {
          submitted_at: new Date().toISOString(),
          current_url: window.location.href,
          user_agent: window.navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        },
      })
      .select("id")
      .single();

    if (error || !data) {
      setSubmitting(false);
      toast.error(error?.message || "Unable to submit feedback.");
      return;
    }

    if (CONTENT_SUGGESTION_TYPES.has(parsed.data.feedbackType)) {
      await supabase.from("content_suggestions").insert({
        feedback_id: data.id,
        user_id: user.id,
        requested_topic: parsed.data.subject,
        requested_course_category: parsed.data.courseId || null,
        rationale: parsed.data.message,
        metadata: {
          feedback_type: parsed.data.feedbackType,
          lesson_id: parsed.data.lessonId || null,
          module_key: parsed.data.moduleKey || null,
        },
      } as never);
    }

    setSubmitting(false);
    setSuccessReference(buildReferenceCode(data.id));

    // Notify the HeirWise team by email (non-blocking for the user).
    void supabase.functions
      .invoke("notify-feedback", { body: { feedbackId: data.id } })
      .catch((err) => console.warn("[FeedbackCenter] notification failed", err));

    setFormState({
      feedbackType: formState.feedbackType,
      subject: "",
      message: "",
      courseId: "",
      lessonId: "",
      moduleKey: "",
      rating: null,
      sentiment: null,
      importanceLevel: null,
    });
    toast.success("Feedback recorded. Thank you.");
    await loadFeedback();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="container mx-auto flex max-w-6xl items-center gap-3 px-4 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">HeirWise product voice</p>
            <h1 className="font-heading text-xl font-bold text-foreground">Feedback & Suggestions</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl space-y-6 px-4 py-6">
        <section className="glass-card p-6">
          <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr] lg:items-center">
            <div className="space-y-4">
              <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">Help us improve</p>
              <h2 className="font-heading text-4xl font-bold text-foreground">Tell us what should feel clearer, smarter, or more ambitious.</h2>
              <p className="max-w-2xl text-sm leading-7 text-muted-foreground">
                Share feature requests, new course ideas, lesson improvements, confusing vocabulary, and product friction that the HeirWise team should prioritize next.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {featuredFeedbackTypes.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormState((current) => ({ ...current, feedbackType: option.value }))}
                  className={cn(
                    "rounded-3xl border p-4 text-left transition-transform duration-200 hover:-translate-y-0.5 hover:bg-card",
                    formState.feedbackType === option.value ? "border-primary/30 bg-primary/10" : "border-border/70 bg-background/55"
                  )}
                >
                  <p className="text-sm font-semibold text-foreground">{option.label}</p>
                  <p className="mt-2 text-xs leading-6 text-muted-foreground">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.05fr,0.95fr]">
          <section className="space-y-6">
            <div className="glass-card p-6">
              <div className="mb-5 flex items-start gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                  <MessageSquareHeart className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Share feedback</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Your ideas help shape future HeirWise updates, learning content, and interface improvements.</p>
                </div>
              </div>

              {successReference && (
                <div className="mb-5 rounded-3xl border border-primary/30 bg-primary/10 p-5">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">Feedback recorded</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Thank you — your input is now part of the product feedback queue. Reference <span className="font-semibold text-foreground">{successReference}</span> appears below in your history.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="feedback-type">Feedback type</Label>
                    <Select value={formState.feedbackType} onValueChange={(value) => setFormState((current) => ({ ...current, feedbackType: value as FeedbackType }))}>
                      <SelectTrigger id="feedback-type">
                        <SelectValue placeholder="Choose feedback type" />
                      </SelectTrigger>
                      <SelectContent>
                        {feedbackTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-subject">Title</Label>
                    <Input id="feedback-subject" value={formState.subject} onChange={(event) => setFormState((current) => ({ ...current, subject: event.target.value }))} placeholder="Example: Add more parent resources for budgeting" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedback-message">Detailed message</Label>
                  <Textarea
                    id="feedback-message"
                    value={formState.message}
                    onChange={(event) => setFormState((current) => ({ ...current, message: event.target.value }))}
                    placeholder="What can we do better, what was confusing, or what would you like added?"
                    className="min-h-[160px]"
                    required
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="feedback-course">Course reference</Label>
                    <Input id="feedback-course" value={formState.courseId} onChange={(event) => setFormState((current) => ({ ...current, courseId: event.target.value }))} placeholder="Optional course" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-lesson">Lesson reference</Label>
                    <Input id="feedback-lesson" value={formState.lessonId} onChange={(event) => setFormState((current) => ({ ...current, lessonId: event.target.value }))} placeholder="Optional lesson" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-module">Module reference</Label>
                    <Input id="feedback-module" value={formState.moduleKey} onChange={(event) => setFormState((current) => ({ ...current, moduleKey: event.target.value }))} placeholder="Optional module" />
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Rating</Label>
                    <div className="flex flex-wrap gap-2">
                      {ratingOptions.map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setFormState((current) => ({ ...current, rating: current.rating === value ? null : value }))}
                          className={cn(
                            "inline-flex h-10 w-10 items-center justify-center rounded-2xl border text-sm font-semibold transition-colors",
                            formState.rating === value ? "border-primary/30 bg-primary/10 text-primary" : "border-border/70 bg-background/55 text-muted-foreground"
                          )}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Sentiment</Label>
                    <Select value={formState.sentiment === null ? "unset" : String(formState.sentiment)} onValueChange={(value) => setFormState((current) => ({ ...current, sentiment: value === "unset" ? null : Number(value) }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Optional sentiment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">No sentiment selected</SelectItem>
                        {sentimentOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Importance</Label>
                    <Select value={formState.importanceLevel === null ? "unset" : String(formState.importanceLevel)} onValueChange={(value) => setFormState((current) => ({ ...current, importanceLevel: value === "unset" ? null : Number(value) }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="How important is this?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">No importance selected</SelectItem>
                        {importanceOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded-3xl border border-border/70 bg-background/55 p-4 text-xs leading-6 text-muted-foreground">
                  Content suggestion prompts can include new lesson topics, modules, full courses, case studies, vocabulary lessons, simulations, parent resources, or help-center improvements.
                </div>

                <Button type="submit" disabled={submitting}>
                  {submitting ? "Submitting feedback..." : "Submit feedback"}
                </Button>
              </form>
            </div>

            <div className="glass-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-accent/30 bg-accent/10 text-accent">
                  <Lightbulb className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">What can you suggest?</h2>
                  <p className="text-sm text-muted-foreground">Use this space for product ideas and curriculum direction, not urgent bugs or account issues.</p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  "New lesson topics",
                  "New modules",
                  "Full course requests",
                  "Additional case studies",
                  "Vocabulary improvements",
                  "More simulations",
                  "Parent resources",
                  "Help-center improvements",
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <div className="glass-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-secondary/30 bg-secondary/10 text-secondary">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">My feedback history</h2>
                  <p className="text-sm text-muted-foreground">See what you’ve already shared and how it is progressing.</p>
                </div>
              </div>

              <div className="space-y-3">
                {loadingFeedback ? (
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Loading your feedback...</div>
                ) : feedbackRows.length === 0 ? (
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">No feedback submitted yet. Your first idea can go straight to the HeirWise product queue.</div>
                ) : (
                  feedbackRows.map((feedback) => {
                    const contentSuggestion = contentSuggestions[feedback.id];
                    return (
                      <div key={feedback.id} className="rounded-3xl border border-border/70 bg-background/55 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">{feedback.subject}</p>
                              <span className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium", statusStyles[feedback.status])}>
                                {FEEDBACK_STATUS_LABELS[feedback.status]}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">{buildReferenceCode(feedback.id)} · {new Date(feedback.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            <p>{feedbackTypeOptions.find((option) => option.value === feedback.feedback_type)?.label || feedback.feedback_type}</p>
                            {feedback.importance_level && <p className="mt-1">Importance {feedback.importance_level}/4</p>}
                          </div>
                        </div>

                        <div className="mt-3 rounded-2xl border border-border/60 bg-card/50 p-3 text-sm text-muted-foreground line-clamp-3">
                          {feedback.message}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                          {feedback.rating && <span className="rounded-full border border-border/70 bg-card px-2.5 py-1">{feedback.rating}/5 rating</span>}
                          {contentSuggestion && <span className="rounded-full border border-border/70 bg-card px-2.5 py-1">Content request tracked</span>}
                          {feedback.course_id && <span className="rounded-full border border-border/70 bg-card px-2.5 py-1">Course: {feedback.course_id}</span>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                  <Star className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Need product help instead?</h2>
                  <p className="text-sm text-muted-foreground">Urgent bugs, login failures, or billing issues belong in Help & Support so the team can act faster.</p>
                </div>
              </div>
              <Button className="w-full" onClick={() => navigate("/support")}>Open Help & Support</Button>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default FeedbackCenter;
